import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import ProductsService from "../api/services/products.service";
import { useCurrencies } from "./CurrenciesContext";
import { useAuth } from "./AuthContext";

interface BasketWishlistContextType {
  basketCount: number;
  wishlistCount: number;
  basketTotalPrice: number;
  setBasketTotalPrice: (price: number) => void;
  increaseBasket: (amount?: number) => void;
  decreaseBasket: (amount?: number) => void;
  increaseWishlist: (amount?: number) => void;
  decreaseWishlist: (amount?: number) => void;
  refreshCounts: () => Promise<void>;
}

const BasketWishlistContext = createContext<
  BasketWishlistContextType | undefined
>(undefined);

export const BasketWishlistProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { currencyId } = useCurrencies();
  const { isLoggedIn } = useAuth();

  const [basketCount, setBasketCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [basketTotalPrice, setBasketTotalPrice] = useState(0);

  const refreshCounts = useCallback(async () => {
    try {
      if (isLoggedIn) {
        const [basketRes, wishlistRes] = await Promise.all([
          ProductsService.getAllBAsket(Number(currencyId)),
          ProductsService.getAllWishlist(Number(currencyId)),
        ]);
        const basketItems = basketRes?.data || [];
        const totalQuantity = basketItems.reduce(
          (sum: number, item: any) => sum + (item.quantity || 0),
          0,
        );
        const totalPriceRaw = basketItems.reduce((sum: number, item: any) => {
          const unitPrice =
            item.product?.discount_price ?? item.product?.price ?? 0;
          return sum + unitPrice * (item.quantity || 0);
        }, 0);
        const totalPrice = Math.round(totalPriceRaw * 100) / 100;
        setBasketCount(totalQuantity);
        setWishlistCount(wishlistRes?.data?.length || 0);
        setBasketTotalPrice(totalPrice);
      } else {
        const guestBasket = JSON.parse(
          localStorage.getItem("guest_basket") || "[]",
        );
        const totalGuestQuantity = guestBasket.reduce(
          (sum: number, item: any) => sum + (item.quantity || 0),
          0,
        );
        const totalGuestPriceRaw = guestBasket.reduce(
          (sum: number, item: any) => {
            const unitPrice =
              item.product?.discount_price ??
              item.product?.price ??
              item.price ??
              0;
            return sum + unitPrice * (item.quantity || 0);
          },
          0,
        );
        const totalGuestPrice = Math.round(totalGuestPriceRaw * 100) / 100;

        setBasketCount(totalGuestQuantity);
        setWishlistCount(0);
        setBasketTotalPrice(totalGuestPrice);
      }
    } catch (error) {
      console.error("Error loading basket/wishlist:", error);
    }
  }, [isLoggedIn, currencyId]);

  const increaseBasket = (amount = 1) =>
    setBasketCount((prev) => prev + amount);
  const decreaseBasket = (amount = 1) =>
    setBasketCount((prev) => Math.max(0, prev - amount));

  const increaseWishlist = (amount = 1) =>
    setWishlistCount((prev) => prev + amount);
  const decreaseWishlist = (amount = 1) =>
    setWishlistCount((prev) => Math.max(0, prev - amount));

  useEffect(() => {
    refreshCounts();
  }, [isLoggedIn, currencyId, refreshCounts]);

  return (
    <BasketWishlistContext.Provider
      value={{
        basketCount,
        wishlistCount,
        basketTotalPrice,
        setBasketTotalPrice,
        increaseBasket,
        decreaseBasket,
        increaseWishlist,
        decreaseWishlist,
        refreshCounts,
      }}
    >
      {children}
    </BasketWishlistContext.Provider>
  );
};

export const useBasketWishlist = () => {
  const context = useContext(BasketWishlistContext);
  if (!context) {
    throw new Error(
      "useBasketWishlist must be used within a BasketWishlistProvider",
    );
  }
  return context;
};
